import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import httpClient, {
  clearStoredSession,
  getStoredSession,
  persistSessionTokens,
} from "../api/httpClient";

const AuthContext = createContext(null);

const normalizeRole = (role) => {
  if (!role || typeof role !== "string") {
    return "DIPENDENTE";
  }

  const upperRole = role.toUpperCase();
  if ([ "SUPERADMIN", "ADMIN", "MANAGER", "DIPENDENTE"].includes(upperRole)) {
    return upperRole;
  }

  return "DIPENDENTE";
};

const decodeJwtPayload = (token) => {
  try {
    const parts = token.split(".");
    if (parts.length < 2) {
      return null;
    }

    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const decoded = JSON.parse(window.atob(base64));
    return decoded;
  } catch (error) {
    return null;
  }
};

const deriveUserFromToken = (token) => {
  const payload = decodeJwtPayload(token);
  if (!payload) {
    return {
      id: null,
      name: "Utente",
      email: "",
      role: "DIPENDENTE",
    };
  }

  return {
    id: payload.sub || payload.userId || payload.id || null,
    name: payload.name || payload.fullName || payload.username || payload.sub || "Utente",
    email: payload.email || "",
    role: normalizeRole(payload.role || payload.ruolo),
  };
};

const buildUserFromAuthPayload = (payload = {}, fallbackIdentity = {}) => {
  const nextToken = payload.token || payload.accessToken || payload.jwt || "";
  const derivedUser = nextToken ? deriveUserFromToken(nextToken) : null;
  const apiUser = payload.user || payload.utente;

  if (apiUser) {
    return {
      id: apiUser.id || apiUser.userId || null,
      name: apiUser.name || apiUser.fullName || apiUser.username || fallbackIdentity.name || "Utente",
      email: apiUser.email || fallbackIdentity.email || "",
      role: normalizeRole(apiUser.role || apiUser.ruolo),
      companyId: apiUser.companyId || apiUser.aziendaId || payload.aziendaId || null,
      companyName: apiUser.companyName || apiUser.nomeAzienda || payload.nomeAzienda || fallbackIdentity.companyName || "",
    };
  }

  return {
    ...(derivedUser || {}),
    id:
      payload.userId ||
      payload.adminId ||
      payload.id ||
      derivedUser?.id ||
      null,
    name:
      payload.username ||
      payload.email ||
      fallbackIdentity.name ||
      derivedUser?.name ||
      "Utente",
    email: payload.email || fallbackIdentity.email || derivedUser?.email || "",
    role: normalizeRole(payload.ruolo || payload.role || derivedUser?.role),
    companyId: payload.aziendaId || payload.companyId || derivedUser?.companyId || null,
    companyName: payload.nomeAzienda || payload.companyName || fallbackIdentity.companyName || derivedUser?.companyName || "",
  };
};

const initialToken = getStoredSession().token;
const savedUserRaw = localStorage.getItem("user");

let initialUser = null;
if (savedUserRaw) {
  try {
    const parsed = JSON.parse(savedUserRaw);
    initialUser = {
      ...parsed,
      role: normalizeRole(parsed?.role),
    };
  } catch (error) {
    initialUser = null;
  }
}

if (!initialUser && initialToken) {
  initialUser = deriveUserFromToken(initialToken);
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(initialToken || "");
  const [refreshToken, setRefreshToken] = useState(getStoredSession().refreshToken || "");
  const [user, setUser] = useState(initialUser);

  const persistSession = useCallback((nextToken, nextRefreshToken, nextUser) => {
    persistSessionTokens({ token: nextToken, refreshToken: nextRefreshToken });
    localStorage.setItem("user", JSON.stringify(nextUser));
    setToken(nextToken);
    setRefreshToken(nextRefreshToken || "");
    setUser(nextUser);
  }, []);

  const clearSession = useCallback(() => {
    clearStoredSession();
    setToken("");
    setRefreshToken("");
    setUser(null);
  }, []);

  const login = useCallback(
    async (credentials) => {
      const response = await httpClient.post("/auth/login", credentials);
      const payload = response?.data || {};

      const nextToken = payload.token || payload.accessToken || payload.jwt;
      const nextRefreshToken = payload.refreshToken || payload.refresh_token || "";
      if (!nextToken) {
        throw new Error("Token JWT non presente nella risposta di login.");
      }

      const nextUser = buildUserFromAuthPayload(payload, {
        name: credentials?.username || "Utente",
      });

      persistSession(nextToken, nextRefreshToken, nextUser);
      return nextUser;
    },
    [persistSession]
  );

  const register = useCallback(
    async (registrationData) => {
      const response = await httpClient.post("/public/register", {
        aziendaNome: registrationData.aziendaNome,
        email: registrationData.email,
        password: registrationData.password,
      });
      const payload = response?.data || {};

      const nextToken = payload.token || payload.accessToken || payload.jwt;
      const nextRefreshToken = payload.refreshToken || payload.refresh_token || "";
      if (!nextToken) {
        throw new Error("Token JWT non presente nella risposta di registrazione.");
      }

      const nextUser = buildUserFromAuthPayload(payload, {
        name: registrationData.email,
        email: registrationData.email,
        companyName: registrationData.aziendaNome,
      });

      persistSession(nextToken, nextRefreshToken, nextUser);
      return nextUser;
    },
    [persistSession]
  );

  const logout = useCallback(
    async (options = {}) => {
      const { allDevices = false, deviceId = null } = options;

      try {
        if (allDevices) {
          await httpClient.post("/auth/logout-all");
        } else {
          await httpClient.post("/auth/logout", {
            refreshToken: refreshToken || undefined,
            logoutAllDevices: false,
            deviceId: deviceId || undefined,
          });
        }
      } catch (error) {
        // Ignore API errors and clear local session anyway.
      } finally {
        clearSession();
      }
    },
    [clearSession, refreshToken]
  );

  const hasAnyRole = useCallback(
    (roles = []) => {
      if (!roles.length) {
        return !!token;
      }

      const normalizedRoles = roles.map((role) => normalizeRole(role));
      const userRole = normalizeRole(user?.role);
      return normalizedRoles.includes(userRole);
    },
    [token, user]
  );

  useEffect(() => {
    const onSessionRefreshed = (event) => {
      const refreshedToken = event?.detail?.token || getStoredSession().token;
      const refreshedRefreshToken = event?.detail?.refreshToken || getStoredSession().refreshToken;

      if (refreshedToken) {
        setToken(refreshedToken);
        setRefreshToken(refreshedRefreshToken || "");
      }
    };

    window.addEventListener("app:session-refreshed", onSessionRefreshed);
    return () => {
      window.removeEventListener("app:session-refreshed", onSessionRefreshed);
    };
  }, []);

  const value = useMemo(
    () => ({
      token,
      refreshToken,
      user,
      isAuthenticated: !!token,
      login,
      register,
      logout,
      hasAnyRole,
    }),
    [token, refreshToken, user, login, register, logout, hasAnyRole]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth deve essere usato dentro AuthProvider");
  }

  return context;
}
